import { resolveAuthDisplayName } from '@/pages/ipe/workbench/utils/resolveAuthDisplayName';

const IPE_WORKSPACE_MODE_KEY = 'ipe_workspace_mode';

const CREATOR_FIELD_KEYS = [
  'ownerName',
  'ownerId',
  'ownerUserId',
  'createdBy',
  'createdByName',
  'workspaceMode',
  'captureSpace',
  'teamId',
];

/** 工作区字段仅在上传时通过显式 creatorPayload 写入，避免 batchSave 误改归属空间 */
const WORKSPACE_FIELD_KEYS = new Set(['workspaceMode', 'captureSpace', 'teamId']);

let imageSpaceCreatorContext = null;

function normalizeWorkspaceMode(mode) {
  return String(mode || 'personal').trim().toLowerCase() === 'team' ? 'team' : 'personal';
}

function readWorkspaceMode() {
  if (typeof window === 'undefined') return 'personal';
  const mode = String(localStorage.getItem(IPE_WORKSPACE_MODE_KEY) || 'personal').trim().toLowerCase();
  return mode === 'team' ? 'team' : 'personal';
}

export function setImageSpaceCreatorContext(context) {
  imageSpaceCreatorContext = context && typeof context === 'object' ? context : null;
}

export function getImageSpaceCreatorContext() {
  return imageSpaceCreatorContext;
}

export function buildImageSpaceCreatorPayload(user, authUserId, workspaceMode, teamId = null) {
  const ownerName = resolveAuthDisplayName(user);
  const mode = normalizeWorkspaceMode(workspaceMode ?? readWorkspaceMode());
  const payload = {
    ownerName,
    ownerId: authUserId ?? null,
    ownerUserId: authUserId ?? null,
    createdBy: ownerName,
    createdByName: ownerName,
    workspaceMode: mode,
    captureSpace: mode,
  };
  if (mode === 'team' && teamId != null && teamId !== '') {
    payload.teamId = teamId;
  }
  return payload;
}

export function syncImageSpaceCreatorContext(user, authUserId, teamId = null) {
  if (!user || authUserId == null || authUserId === '') {
    setImageSpaceCreatorContext(null);
    return null;
  }
  const payload = buildImageSpaceCreatorPayload(user, authUserId, readWorkspaceMode(), teamId ?? user?.teamId ?? null);
  setImageSpaceCreatorContext(payload);
  return payload;
}

export function getActiveImageSpaceCreatorPayload() {
  if (imageSpaceCreatorContext?.ownerName || imageSpaceCreatorContext?.createdByName) {
    const mode = readWorkspaceMode();
    return {
      ...imageSpaceCreatorContext,
      workspaceMode: mode,
      captureSpace: mode,
    };
  }
  return null;
}

function pickFirstCreatorLabel(image) {
  return String(
    image?.createdByName
    || image?.created_by_name
    || image?.createdBy
    || image?.created_by
    || image?.ownerName
    || image?.owner_name
    || image?.creator
    || '',
  ).trim();
}

export function attachImageSpaceCreator(record, creatorPayload, { allowWorkspaceFields = false } = {}) {
  if (!record || typeof record !== 'object') return record;
  if (!creatorPayload || typeof creatorPayload !== 'object') return { ...record };

  const next = { ...record };
  CREATOR_FIELD_KEYS.forEach((key) => {
    if (!allowWorkspaceFields && WORKSPACE_FIELD_KEYS.has(key)) return;
    const incoming = creatorPayload[key];
    const current = next[key];
    if (incoming == null || incoming === '') return;
    if (current == null || current === '') {
      next[key] = incoming;
    }
  });
  return next;
}

export function enrichImageSpaceRecords(records, explicitCreatorPayload = null) {
  const list = Array.isArray(records) ? records : [];
  const creatorPayload = explicitCreatorPayload || getActiveImageSpaceCreatorPayload();
  if (!creatorPayload) return list;
  const allowWorkspaceFields = Boolean(explicitCreatorPayload);
  return list.map((record) => attachImageSpaceCreator(record, creatorPayload, { allowWorkspaceFields }));
}

function resolveImageSpaceOwnerId(image) {
  const raw = image?.ownerId ?? image?.owner_id ?? image?.ownerUserId
    ?? image?.createdByUserId ?? image?.userId ?? image?.user_id ?? '';
  return raw != null && raw !== '' ? String(raw) : '';
}

export function resolveImageSpaceCreatorLabel(image, { authUserId = '', authDisplayName = '' } = {}) {
  const label = pickFirstCreatorLabel(image);
  if (label) return label;

  const ownerId = resolveImageSpaceOwnerId(image);
  const viewerId = authUserId != null && authUserId !== '' ? String(authUserId) : '';
  if (viewerId && ownerId && ownerId === viewerId) {
    const fallback = String(authDisplayName || '').trim();
    return fallback || '我';
  }
  return '—';
}
